"use client";

import { Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { Icon } from "@/components/ui/icon";
import { platformOverview } from "@/lib/data/homepage";
import { PlatformEcosystem, TierPyramid } from "./visuals";

/**
 * Platform Overview — Asymmetric editorial layout.
 * Left: full-bleed platform ecosystem map (50+ platforms).
 * Right: tier pyramid (4 deployment tiers) + 3 feature callouts.
 */
export function PlatformOverview() {
  return (
    <section id="platform-overview" className="relative section-pad">
      <Container>
        <Reveal className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass eyebrow-tiny text-[var(--color-cyan)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-cyan)]" />
            {platformOverview.eyebrow}
          </span>
          <h2 className="mt-6">
            {platformOverview.title}
          </h2>
          <p className="mt-4 text-body leading-relaxed text-[rgb(var(--fg-muted))]">
            {platformOverview.intro}
          </p>
        </Reveal>

        {/* Top row: ecosystem + tier pyramid */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="rounded-3xl glass p-8 h-full relative overflow-hidden card-glow" style={{ ["--glow" as string]: "var(--color-cyan)" }}>
              <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-[var(--color-cyan)] opacity-15 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="eyebrow-tiny text-[var(--color-cyan)] mb-2">
                  50+ Platforms · Zero Blind Spots
                </div>
                <h3 className="mb-2">
                  The Perception Surface
                </h3>
                <p className="text-sm text-[rgb(var(--fg-muted))] mb-6 max-w-md">
                  Every digital surface that shapes stakeholder opinion. From social to search, news to forums.
                </p>
                <div className="flex justify-center">
                  <PlatformEcosystem />
                </div>
                <div className="mt-6 text-center">
                  <div className="text-5xl font-bold font-mono-num leading-none" style={{ color: "var(--color-cyan)" }}>
                    50+
                  </div>
                  <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold">
                    Platforms Covered
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="rounded-3xl glass p-8 h-full relative overflow-hidden card-glow" style={{ ["--glow" as string]: "var(--color-crimson)" }}>
              <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-[var(--color-crimson)] opacity-15 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="eyebrow-tiny text-[var(--color-crimson)] mb-2">
                  4 Deployment Tiers
                </div>
                <h3 className="mb-2">
                  Scale to Your Requirements
                </h3>
                <p className="text-sm text-[rgb(var(--fg-muted))] mb-6 max-w-md">
                  From individual protection to sovereign operations — every tier maintains 95–98% organic authenticity.
                </p>
                <TierPyramid />
              </div>
            </div>
          </Reveal>
        </div>

        {/* Bottom row: 3 feature callouts */}
        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          {platformOverview.features.map((f, i) => (
            <Reveal key={f.title} delay={i * 80}>
              <div className="rounded-2xl glass p-5 h-full hover:scale-[1.02] transition-transform card-glow" style={{ ["--glow" as string]: i === 0 ? "var(--color-purple)" : i === 1 ? "var(--color-emerald)" : "var(--color-amber)" }}>
                <Icon name={f.icon} className="h-7 w-7 mb-3" style={{ color: i === 0 ? "var(--color-purple)" : i === 1 ? "var(--color-emerald)" : "var(--color-amber)" }} />
                <h3>{f.title}</h3>
                <p className="mt-2 text-body-xs leading-relaxed text-[rgb(var(--fg-muted))]">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}
